// SPDX-License-Identifier: MIT

// Theme resolution + Google Consent Mode helpers, shared by the React UI
// (theming) and gatsby-ssr.js (the Consent Mode default script). Brand-neutral:
// sites supply their colors/font through the consent config, falling back to the
// `classic` scheme below.

// ---------------------------------------------------------------------------
// Color scheme presets
// ---------------------------------------------------------------------------
// A preset supplies background/text/primary only when the matching individual
// color isn't provided (see resolveTheme). `classic` is the neutral default.
const COLOR_PRESETS = {
  classic: { background: '#FFFFFF', text: '#4B494B', primary: '#533BE2' },
  amber: { background: '#050505', text: '#F4F0DF', primary: '#F9D000' },
  neutral: { background: '#FFFFFF', text: '#4B494B', primary: '#3B82F6' },
  twilight: { background: '#0B1020', text: '#E5F2FB', primary: '#8EC9F0' },
};

// Defaults when neither an individual color nor a preset is given.
const DEFAULT_COLORS = COLOR_PRESETS.classic;
const DEFAULT_FONT_FAMILY = 'Helvetica Neue, Segoe UI, Arial, sans-serif';
const DEFAULT_BOX_SHADOW = '-5px 5px 10px 0px #00000012, 0px 0px 50px 0px #0000001a';

// First defined (non-null) value, for option → preset → default fallback chains.
const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

// Combines a solid #RRGGBB color with a 0–1 opacity into #RRGGBBAA (for the
// backdrop). Non-6-digit-hex colors are returned unchanged.
const toHex8 = (color, opacity) => {
  const hex = String(color).trim();
  if (opacity === undefined || opacity === null) return hex;
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
  return `${hex}${alpha}`;
};

// ---------------------------------------------------------------------------
// Theme → CSS custom properties
// ---------------------------------------------------------------------------
// Per-color precedence: individual `colors.<name>` > `colors.preset` > default.
const resolveTheme = (config = {}) => {
  const colors = config.colors || {};
  const preset = colors.preset ? COLOR_PRESETS[colors.preset] : null;
  const pick = (name, fallback) =>
    firstDefined(colors[name], preset && preset[name], fallback);

  const primary = pick('primary', DEFAULT_COLORS.primary);
  const background = pick('background', DEFAULT_COLORS.background);
  const text = pick('text', DEFAULT_COLORS.text);
  const backdrop = config.backdrop || {};

  return {
    boxShadow: firstDefined(config.boxShadow, DEFAULT_BOX_SHADOW),
    fontFamily: firstDefined(config.font, DEFAULT_FONT_FAMILY),
    primaryColor: primary,
    backgroundColor: background,
    textColor: text,
    backdropBackgroundColor: toHex8(
      firstDefined(backdrop.color, '#000000'),
      firstDefined(backdrop.opacity, 0.5)
    ),
    backdropBackgroundBlur: `${firstDefined(backdrop.blur, 0)}px`,
    iconColor: firstDefined(colors.icon, primary),
    iconBackgroundColor: firstDefined(colors.iconBackground, '#FFFFFF'),
    ...(config.theme || {}),
  };
};

// Builds the `#cc-root { --var: value; }` rule the components read.
const buildThemeCss = (theme) => {
  const declarations = Object.entries(theme)
    .map(([name, value]) => `  --${name}: ${value};`)
    .join('\n');
  return `#cc-root {\n${declarations}\n}`;
};

// ---------------------------------------------------------------------------
// Google Consent Mode defaults
// ---------------------------------------------------------------------------
// Consent Mode treats anything not defaulted to `denied` as granted, so without
// this Google tags set cookies on load regardless of the banner. gatsby-ssr.js
// emits it at the very front of <head>, before gtag/GTM; the React runtime then
// sends `update` calls when the visitor chooses.
const DEFAULT_CONSENT_MODE = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
  wait_for_update: 500,
};

const buildConsentModeScript = (states) =>
  [
    'window.dataLayer = window.dataLayer || [];',
    'function gtag(){dataLayer.push(arguments);}',
    `gtag('consent', 'default', ${JSON.stringify(states)});`,
  ].join('\n');

module.exports = {
  COLOR_PRESETS,
  DEFAULT_COLORS,
  DEFAULT_CONSENT_MODE,
  firstDefined,
  resolveTheme,
  buildThemeCss,
  buildConsentModeScript,
};
